import { Component, computed, DestroyRef, ElementRef, inject, input } from '@angular/core';
import { RidScrapbookService } from '../../services/rid-scrapbook.service';
import { ERASER_WIDTH, PENCIL_WIDTH, RidDrawingTool } from '../rid-drawing-tray/rid-drawing-tray';

// points closer together than this are dropped, which keeps the save small without visibly changing the line
const MIN_POINT_DISTANCE = 2;

/**
 * Lies on top of a week's page (above the stickers, so strokes can start on them) while a drawing tool is picked up, and turns pointer movement into strokes.
 * Coordinates are the same content box pixels as the placed rewards, so it has to cover the whole content box.
 */
@Component({
  selector: 'rid-scrapbook-drawing-input',
  template: '',
  host: {
    class: 'absolute inset-0 z-[2147483647] touch-none',
    '[style.cursor]': 'cursor()',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'onPointerUp($event)',
    '(pointercancel)': 'onPointerUp($event)',
  },
})
export class RidScrapbookDrawingInput {
  private readonly scrapbook = inject(RidScrapbookService);
  private readonly el: HTMLElement = inject(ElementRef).nativeElement;

  week = input.required<number>();
  tool = input.required<RidDrawingTool>();

  protected readonly cursor = computed(() => {
    const tool = this.tool();
    const size = tool.kind === 'eraser' ? ERASER_WIDTH : PENCIL_WIDTH + 4;
    const half = size / 2;
    const fill = tool.kind === 'eraser' ? 'rgba(255,255,255,0.4)' : tool.color;
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size + 2}" height="${size + 2}">` +
      `<circle cx="${half + 1}" cy="${half + 1}" r="${half}" fill="${fill}" stroke="black" stroke-opacity="0.5" stroke-width="1.5"/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${half + 1} ${half + 1}, crosshair`;
  });

  private pointerId: number | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.pointerId !== null) this.scrapbook.activeStroke.set(null);
    });
  }

  protected onPointerDown(event: PointerEvent) {
    if (this.pointerId !== null || event.button !== 0) return;

    event.preventDefault();
    this.pointerId = event.pointerId;
    this.el.setPointerCapture(event.pointerId);

    const tool = this.tool();
    this.scrapbook.activeStroke.set({
      week: this.week(),
      color: tool.kind === 'eraser' ? null : tool.color,
      width: tool.kind === 'eraser' ? ERASER_WIDTH : PENCIL_WIDTH,
      points: this.toLocal(event),
    });
  }

  protected onPointerMove(event: PointerEvent) {
    if (event.pointerId !== this.pointerId) return;

    const active = this.scrapbook.activeStroke();
    if (!active) return;

    const points = [...active.points];
    for (const e of event.getCoalescedEvents?.() ?? [event]) {
      const [x, y] = this.toLocal(e);
      const lastX = points[points.length - 2];
      const lastY = points[points.length - 1];
      if (Math.hypot(x - lastX, y - lastY) >= MIN_POINT_DISTANCE) points.push(x, y);
    }
    if (points.length !== active.points.length) this.scrapbook.activeStroke.set({ ...active, points });
  }

  protected onPointerUp(event: PointerEvent) {
    if (event.pointerId !== this.pointerId) return;

    this.pointerId = null;
    const active = this.scrapbook.activeStroke();
    this.scrapbook.activeStroke.set(null);
    if (active) this.scrapbook.addItem(active.week, { type: 'DRAWING', color: active.color, width: active.width, points: active.points });
  }

  // offset sizes ignore transforms, so this also holds when the book is scaled
  private toLocal(event: PointerEvent): [number, number] {
    const rect = this.el.getBoundingClientRect();
    const x = ((event.clientX - rect.left) * this.el.offsetWidth) / (rect.width || 1);
    const y = ((event.clientY - rect.top) * this.el.offsetHeight) / (rect.height || 1);
    return [Math.round(x), Math.round(y)];
  }
}
